import {noop} from '@benchling/js-utils';
import {useCallback, useEffect} from 'react';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {TableLocationNameArgs} from 'notebook/tables/excess/EvaluationConstants';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';

/**
 * Implements the multi-window editing behavior of MultiSelectListUtil.
 *
 * This automatically adds click handlers to the window to handle multi-table editing.
 */
export default function useMultiWindowEditing({
  tableNamespace,
  fileId,
  domNode,
  tableName,
  documentName,
  multiStartIndex,
  multiEndIndex,
  hasFormulasDisabled,
  multiActive,
  multiEditing,
  onEditFinalize,
  setState,
  canInsert,
  setMultiEditingForTable,
}: {
  tableNamespace: string;
  fileId: string;
  documentName: string | null;
  domNode: Element;
  tableName: string;
  multiStartIndex: TableIndex | null;
  multiEndIndex: TableIndex | null;
  hasFormulasDisabled: boolean;
  multiActive: string | boolean | null;
  multiEditing: boolean;
  onEditFinalize: () => void;
  setState?: (state: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>>) => void;
  // If canInsert/setMultiEditingForTable are not provided, they will automatically use the default
  // implementation from SpreadsheetEvalUtil.
  canInsert?: (e: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  setMultiEditingForTable?: (
    {tableName, documentName}: TableLocationNameArgs,
    e: {index: TableIndex | null; editing: boolean; text: string}
  ) => void;
}): void {
  const multiCanInsert = useCallback(
    (e: {start: number; end: number; cid: string; shift: boolean}) => {
      if (canInsert) {
        return canInsert(e);
      } else {
        return SpreadsheetEvalUtil.canInsert({
          e,
          hasFormulasDisabled,
          tableNamespace,
          editingTable: spreadsheetStore.getState(tableNamespace).editingTable,
          editingDocumentName: spreadsheetStore.getState(tableNamespace).editingDocumentName,
        });
      }
    },
    [tableNamespace, hasFormulasDisabled, canInsert]
  );

  const setMultiEditing = useCallback(
    (args: {index: TableIndex | null; editing: boolean; text: string}) => {
      if (setMultiEditingForTable) {
        setMultiEditingForTable({tableName, documentName}, args);
      } else {
        SpreadsheetEvalUtil.setMultiEditingForTable({tableName, documentName}, tableNamespace, fileId, {
          index: args.index,
          editing: args.editing,
          text: args.text,
          typedValue: null,
          selection: null,
        });
      }
    },
    [tableNamespace, tableName, documentName, fileId, setMultiEditingForTable]
  );

  const _multiWindowMouseDownHandler = useCallback(
    (e: MouseEvent): void => {
      const options = {
        multiEditing,
        multiCanInsert,
        multiNamespace: tableNamespace,
        multiFinalizeEditHandler: onEditFinalize,
        domNode,
        multiStartIndex,
        multiEndIndex,
        setMultiEditing,
        setState: setState ?? noop,
        multiActive,
        multiCrossEditing: spreadsheetStore.getState(tableNamespace).editingTable != null,
      };
      MultiSelectListUtil.multiWindowMouseDownHandler(e, options);
    },
    [
      multiEditing,
      multiCanInsert,
      tableNamespace,
      onEditFinalize,
      domNode,
      multiStartIndex,
      multiEndIndex,
      setMultiEditing,
      setState,
      multiActive,
    ]
  );

  const _multiWindowMouseUpHandler = useCallback(
    (e: MouseEvent): void => {
      const options = {
        multiEditing,
        multiCanInsert: canInsert,
        multiNamespace: tableNamespace,
        multiFinalizeEditHandler: onEditFinalize,
        domNode,
        multiStartIndex,
        multiEndIndex,
        setMultiEditing,
        setState: setState ?? noop,
        multiActive,
        multiCrossEditing: spreadsheetStore.getState(tableNamespace).editingTable != null,
      };
      MultiSelectListUtil.multiWindowMouseUpHandler(e, options);
    },
    [
      multiEditing,
      canInsert,
      tableNamespace,
      onEditFinalize,
      domNode,
      multiStartIndex,
      multiEndIndex,
      setMultiEditing,
      setState,
      multiActive,
    ]
  );

  useEffect(() => {
    MultiSelectListUtil.multiSelectableDidMount(_multiWindowMouseDownHandler, _multiWindowMouseUpHandler);
    return () => {
      MultiSelectListUtil.multiSelectableWillUnmount(
        _multiWindowMouseDownHandler,
        _multiWindowMouseUpHandler
      );
    };
  }, [_multiWindowMouseDownHandler, _multiWindowMouseUpHandler]);
}
